import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';
import { PlanBo } from '@utils/types/back-office/update-plan-bo.ts';
import { useGetPricePlansBo } from '@utils/api/back-office/price-plans-bo/use-get-price-plans-bo.tsx';
import { useGetPlanBoDetailed } from '@utils/api/back-office/plans-bo/use-get-plan-bo-detailed';

type Params = { planId: string };

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<PlanBo, any>;
  setValue: UseFormSetValue<PlanBo>;
  setBatchEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  setRealtimeEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  setAlignmentEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  setTranslationEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  setTemplateEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  setAutomaticTrainingEnabled: React.Dispatch<React.SetStateAction<boolean>>;
}

const toBool = (v: unknown) =>
  v === true || v === 'true' || v === 1 || v === '1';

export function usePlanDetailsData({
  control,
  setValue,
  setBatchEnabled,
  setRealtimeEnabled,
  setAlignmentEnabled,
  setTranslationEnabled,
  setTemplateEnabled,
  setAutomaticTrainingEnabled
}: Props) {
  const { planId } = useParams<Params>();
  const [searchText, setSearchText] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isFairUseEnabled, setFairUseEnabled] = useState(false);
  const [realtimeMaxStreamsInput, setRealtimeMaxStreamsInput] =
    useState<string>('');
  const [realtimeLanguagesInput, setRealtimeLanguagesInput] =
    useState<string>('');

  const sendExceedanceEmail = useWatch({
    control,
    name: 'send_exceedance_email',
    defaultValue: false
  });
  const selectedSttPlanId = useWatch({ control, name: 'stt_pricing_plan' });
  const selectedAlignmentPlanId = useWatch({
    control,
    name: 'alignment_pricing_plan'
  });
  const selectedTranslationPlanId = useWatch({
    control,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    name: 'translation_pricing_plan' as any
  });

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedQuery(searchText.trim()), 300);
    return () => clearTimeout(timeout);
  }, [searchText]);

  const { data: pricePlans } = useGetPricePlansBo(debouncedQuery);
  const { data: planBo } = useGetPlanBoDetailed(planId);

  useEffect(() => {
    const r = planBo?.result;
    if (!r) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setValue('processing_type', (r.processing_type as any) ?? 'OTHER');

    const toggleSpecs = [
      { key: 'batch_enabled' as const, set: setBatchEnabled },
      { key: 'realtime_enabled' as const, set: setRealtimeEnabled },
      { key: 'alignment_enabled' as const, set: setAlignmentEnabled },
      { key: 'translation_enabled' as const, set: setTranslationEnabled },
      { key: 'templates_exported_enabled' as const, set: setTemplateEnabled },
      {
        key: 'automatic_training_enabled' as const,
        set: setAutomaticTrainingEnabled
      }
    ] as const;

    toggleSpecs.forEach(({ key, set }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const v = toBool((r as any)[key]);
      set(v);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setValue(key, v as any, { shouldValidate: false, shouldDirty: false });
    });

    setValue('stt_limit', Number(r.stt_limit ?? 0));
    setValue('media_unit_size', Number(r.media_unit_size ?? 0));
    setValue('translation_characters', Number(r.translation_characters ?? 0));
    setValue('translation_cost', Number(r.translation_cost ?? 0));

    const translationPlanRaw = (r as any).translation_pricing_plan; // eslint-disable-line @typescript-eslint/no-explicit-any
    const translationPlanId =
      typeof translationPlanRaw === 'object' && translationPlanRaw !== null
        ? (translationPlanRaw as any).id // eslint-disable-line @typescript-eslint/no-explicit-any
        : translationPlanRaw;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setValue('translation_pricing_plan' as any, translationPlanId ?? null, {
      shouldValidate: false,
      shouldDirty: false
    });
    setValue(
      'translation_character_limit',
      r.translation_character_limit != null
        ? Number(r.translation_character_limit)
        : null
    );
    setValue('send_exceedance_email', r.send_exceedance_email ?? false);
    setValue(
      'exceedance_email_recipients',
      r.exceedance_email_recipients ?? ''
    );
    setValue(
      'automatic_training_unit_cost',
      Number(r.automatic_training_unit_cost ?? 0)
    );
    setValue(
      'automatic_training_monthly_cost',
      Number(r.automatic_training_monthly_cost ?? 0)
    );

    setFairUseEnabled(toBool(r.is_fair_use));
    setValue('is_fair_use', toBool(r.is_fair_use));
    setValue('fair_use_rate_limit', r.fair_use_rate_limit ?? undefined);
    setValue(
      'automatic_training_unit_limit',
      r.automatic_training_unit_limit ?? undefined
    );
    setValue(
      'automatic_training_hosting_limit',
      r.automatic_training_hosting_limit ?? undefined
    );
    setValue(
      'custom_model_yearly_limit',
      r.custom_model_yearly_limit ?? undefined
    );

    setValue(
      'realtime_max_concurrent_streams',
      r.realtime_max_concurrent_streams ?? null
    );
    setValue('realtime_languages', r.realtime_languages ?? null);
    setRealtimeMaxStreamsInput(
      r.realtime_max_concurrent_streams != null
        ? String(r.realtime_max_concurrent_streams)
        : ''
    );
    setRealtimeLanguagesInput(
      r.realtime_languages?.length ? r.realtime_languages.join(', ') : ''
    );
  }, [
    planBo,
    setValue,
    setBatchEnabled,
    setRealtimeEnabled,
    setAlignmentEnabled,
    setTranslationEnabled,
    setTemplateEnabled,
    setAutomaticTrainingEnabled
  ]);

  return {
    planBo,
    pricePlans,
    isFairUseEnabled,
    setFairUseEnabled,
    realtimeMaxStreamsInput,
    setRealtimeMaxStreamsInput,
    realtimeLanguagesInput,
    setRealtimeLanguagesInput,
    searchText,
    setSearchText,
    sendExceedanceEmail,
    selectedSttPlanId,
    selectedAlignmentPlanId,
    selectedTranslationPlanId
  };
}
